import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { TESTIDS } from "@/constants/testIds";
import useSeo from "@/hooks/useSeo";

const Datenschutz = () => {
  useSeo({
    title: "Datenschutzerklärung | Prozenthaus Schenklengsfeld",
    description:
      "Datenschutzerklärung nach DSGVO: Informationen zur Verarbeitung personenbezogener Daten auf prozenthaus.shop.",
    canonical: "https://prozenthaus.shop/datenschutz",
    robots: "index, follow",
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <section
      data-testid={TESTIDS.datenschutzPage}
      className="border-b border-[var(--pz-border)]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <Link
          to="/"
          data-testid={TESTIDS.legalBackHome}
          className="inline-flex items-center gap-2 pz-link text-sm mb-10"
        >
          <ArrowLeft size={16} /> Zurück zur Startseite
        </Link>

        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="pz-eyebrow-gold mb-4">Rechtliches</div>
            <h1 className="font-display text-5xl sm:text-6xl font-black leading-[1.02] tracking-tight">
              Datenschutz­erklärung.
            </h1>
            <p className="mt-6 text-[var(--pz-text-muted)] text-base leading-relaxed">
              Informationen zur Verarbeitung personenbezogener Daten gemäß Art. 13
              DSGVO.
            </p>
          </div>

          <div className="lg:col-span-8 pz-legal-content">
            <h2>1. Verantwortlicher</h2>
            <p>
              Verantwortlich für die Datenverarbeitung auf dieser Website ist:
              <br />
              <strong>Prozenthaus</strong>
              <br />
              Inhaber: Ron Kowalewsky
              <br />
              Landeckerstraße 20A
              <br />
              36277 Schenklengsfeld
              <br />
              Telefon: <a href="tel:+4966292160485">06629 2160485</a>
              <br />
              E-Mail:{" "}
              <a href="mailto:team@prozenthaus.shop">team@prozenthaus.shop</a>
            </p>

            <h2>2. Allgemeine Hinweise</h2>
            <p>
              Wir verarbeiten personenbezogene Daten unserer Nutzer grundsätzlich
              nur, soweit dies zur Bereitstellung einer funktionsfähigen Website
              sowie unserer Inhalte und Leistungen erforderlich ist. Die
              Verarbeitung erfolgt regelmäßig nur nach Einwilligung des Nutzers
              oder in den Fällen, in denen eine vorherige Einholung einer
              Einwilligung aus tatsächlichen Gründen nicht möglich ist und die
              Verarbeitung der Daten durch gesetzliche Vorschriften gestattet ist.
            </p>

            <h2>3. Aufruf der Website (Server-Log-Dateien)</h2>
            <p>
              Beim bloßen Aufruf unserer Website werden durch den Browser
              automatisch Informationen an den Server unseres Hosters gesendet.
              Dies sind insbesondere:
            </p>
            <ul>
              <li>IP-Adresse (anonymisiert / gekürzt)</li>
              <li>Datum und Uhrzeit der Anfrage</li>
              <li>Inhalt der Anforderung (konkrete Seite)</li>
              <li>Zugriffsstatus / HTTP-Statuscode</li>
              <li>Jeweils übertragene Datenmenge</li>
              <li>Website, von der die Anforderung kommt (Referrer)</li>
              <li>Browser, Betriebssystem und dessen Oberfläche</li>
            </ul>
            <p>
              Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO. Unser berechtigtes
              Interesse besteht darin, den technischen Betrieb sowie die
              Sicherheit der Website zu gewährleisten.
            </p>

            <h2>4. Kontaktformular und Kontaktaufnahme per E-Mail</h2>
            <p>
              Auf unserer Website bieten wir Ihnen die Möglichkeit, mit uns über
              ein Kontaktformular Kontakt aufzunehmen. Es werden folgende Daten
              verarbeitet:
            </p>
            <ul>
              <li>Name</li>
              <li>E-Mail-Adresse</li>
              <li>Telefonnummer (optional)</li>
              <li>Betreff (optional)</li>
              <li>Ihre Nachricht</li>
            </ul>
            <p>
              Die Verarbeitung dient ausschließlich der Bearbeitung Ihrer Anfrage.
              Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO (vorvertragliche
              Maßnahmen) bzw. lit. f DSGVO (berechtigtes Interesse an der
              effizienten Beantwortung von Anfragen). Ihre Daten werden gelöscht,
              sobald sie für die Erreichung des Zweckes ihrer Erhebung nicht mehr
              erforderlich sind und keine gesetzlichen Aufbewahrungspflichten
              entgegenstehen.
            </p>

            <h2>5. Externe Links (eBay)</h2>
            <p>
              Unsere Website enthält Links zu unserem eBay-Shop
              (ebay.de/str/prozenthaus). Wenn Sie diese Links anklicken, werden Sie
              auf die Website von eBay weitergeleitet. Für die Datenverarbeitung
              auf eBay ist ausschließlich die eBay GmbH bzw. die eBay Inc.
              verantwortlich. Bitte beachten Sie die dortigen
              Datenschutzhinweise.
            </p>

            <h2>6. Hosting</h2>
            <p>
              Diese Website wird bei einem externen Dienstleister gehostet. Die
              personenbezogenen Daten, die auf dieser Website erfasst werden,
              werden auf den Servern des Hosters gespeichert. Hierbei kann es sich
              v. a. um IP-Adressen, Kontaktanfragen, Meta- und
              Kommunikationsdaten, Vertragsdaten, Kontaktdaten, Namen, Websitezugriffe
              und sonstige Daten, die über eine Website generiert werden, handeln.
              Der Einsatz des Hosters erfolgt zum Zwecke der Vertragserfüllung
              gegenüber unseren potenziellen und bestehenden Kunden (Art. 6 Abs. 1
              lit. b DSGVO) und im Interesse einer sicheren, schnellen und
              effizienten Bereitstellung unseres Online-Angebots (Art. 6 Abs. 1
              lit. f DSGVO).
            </p>

            <h2>7. Cookies</h2>
            <p>
              Unsere Website verwendet derzeit nur technisch notwendige Cookies,
              die für den Betrieb der Seite erforderlich sind. Eine Verarbeitung
              durch Tracking- oder Marketing-Cookies findet nicht statt.
            </p>

            <h2>8. Ihre Rechte</h2>
            <p>
              Sie haben jederzeit das Recht:
            </p>
            <ul>
              <li>
                <strong>Auskunft</strong> über Ihre bei uns gespeicherten Daten
                gemäß Art. 15 DSGVO zu verlangen.
              </li>
              <li>
                <strong>Berichtigung</strong> unrichtiger Daten gemäß Art. 16 DSGVO
                zu verlangen.
              </li>
              <li>
                <strong>Löschung</strong> Ihrer bei uns gespeicherten Daten gemäß
                Art. 17 DSGVO zu verlangen.
              </li>
              <li>
                <strong>Einschränkung der Verarbeitung</strong> gemäß Art. 18 DSGVO
                zu verlangen.
              </li>
              <li>
                <strong>Datenübertragbarkeit</strong> gemäß Art. 20 DSGVO zu
                verlangen.
              </li>
              <li>
                <strong>Widerspruch</strong> gegen die Verarbeitung gemäß Art. 21
                DSGVO einzulegen, soweit unsere Verarbeitung auf Art. 6 Abs. 1 lit.
                e oder f DSGVO beruht.
              </li>
              <li>
                Sich bei einer <strong>Aufsichtsbehörde</strong> zu beschweren
                (Art. 77 DSGVO). In Hessen ist dies der Hessische Beauftragte für
                Datenschutz und Informationsfreiheit.
              </li>
            </ul>

            <h2>9. SSL- bzw. TLS-Verschlüsselung</h2>
            <p>
              Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der
              Übertragung vertraulicher Inhalte eine SSL- bzw. TLS-Verschlüsselung.
              Sie erkennen eine verschlüsselte Verbindung an der Adresszeile des
              Browsers (https:// statt http://).
            </p>

            <h2>10. Aktualität dieser Datenschutzerklärung</h2>
            <p>
              Diese Datenschutzerklärung ist aktuell gültig und hat den Stand{" "}
              {new Date().toLocaleDateString("de-DE", {
                year: "numeric",
                month: "long",
              })}
              . Durch die Weiterentwicklung unserer Website oder geänderte
              gesetzliche bzw. behördliche Vorgaben kann es notwendig werden, diese
              Datenschutzerklärung zu ändern.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Datenschutz;
